/**
 * @ Author: Housefolios
 * @ Create Time: 2022-09-02 10:50:32
 * @ Description: Dialog to approve/reject properties submitted to a shared marketplace.
 */

import React, { useState } from 'react'
import {
  Grid, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'

import { enqueueSnackbar } from 'notistack'
import withSession from '@/housefolios-components/Session/withSession'
import { useMutation } from '@apollo/client/react'
import {
  APPROVE_PROPERTIES,
  REJECT_PROPERTIES} from '../mutations'
import { approvalMutationCacheOptions } from '@/utils/marketplaceRefetchQueries'

function resolveMarketplaceURL(marketplace) {
  if (!marketplace || marketplace === 'null') return 'housefolios'
  return marketplace
}

function SubmissionApprovalComponent(props) {
  const { marketplace = '', propertyIds, fullWidth = true } = props
  const marketplaceURL = resolveMarketplaceURL(marketplace)

  const [openAccept, setOpenAccept] = useState(false)
  const [openReject, setOpenReject] = useState(false)

  const [approveProperties] = useMutation(APPROVE_PROPERTIES)
  const [rejectProperties] = useMutation(REJECT_PROPERTIES)

  const afterDecision = (event, variant, message) => {
    enqueueSnackbar(message, {
      variant,
      autoHideDuration: 3000,
    })
    if (props.setMarketplaceSelected) props.setMarketplaceSelected([])
    if (props.removeProperty) props.removeProperty(event)
  }

  return (
    <>
      <Grid
        container
        spacing={1}
        wrap="nowrap"
        size={fullWidth ? 12 : 'auto'}
        sx={{ width: fullWidth ? '100%' : 'auto' }}
      >
        <Grid size={fullWidth ? 6 : 'auto'}>
          <Button
            fullWidth={fullWidth}
            variant="contained"
            style={{ backgroundColor: '#3edf6e', color: 'white' }}
            onClick={() => setOpenAccept(true)}
          >
            Accept
          </Button>
        </Grid>
        <Grid size={fullWidth ? 6 : 'auto'}>
          <Button
            fullWidth={fullWidth}
            variant="contained"
            style={{ backgroundColor: '#ff3f3d', color: 'white' }}
            onClick={() => setOpenReject(true)}
          >
            Reject
          </Button>
        </Grid>
      </Grid>

      <Dialog
        open={openReject}
        onClose={() => setOpenReject(false)}
        maxWidth="sm"
        fullWidth={true}
      >
        <DialogTitle>
          Reject {propertyIds.length > 1 ? 'Properties' : 'Property'}
        </DialogTitle>
        <DialogContent>
          Would you like to reject{' '}
          {propertyIds.length > 1
            ? 'these properties'
            : 'this property'}
          ?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReject(false)}>No</Button>
          <Button
            variant="contained"
            style={{ backgroundColor: '#ff3f3d', color: 'white' }}
            onClick={(event) => {
              rejectProperties({
                variables: {
                  propertyIds,
                  marketplaceURL,
                },
                ...approvalMutationCacheOptions(
                  'rejectProperties',
                  propertyIds,
                ),
              })
                .then((result) => {
                  if (!result?.data?.rejectProperties) {
                    enqueueSnackbar(
                      'Failed to reject property. Please try again.',
                      { variant: 'error', autoHideDuration: 4000 },
                    )
                    return
                  }
                  afterDecision(
                    event,
                    'error',
                    `${
                      propertyIds.length > 1
                        ? 'Properties'
                        : 'Property'
                    } has been rejected`,
                  )
                })
                .catch((error) => {
                  enqueueSnackbar(
                    error?.message ||
                      'Failed to reject property. Please try again.',
                    { variant: 'error', autoHideDuration: 4000 },
                  )
                })
              setOpenReject(false)
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAccept}
        onClose={() => setOpenAccept(false)}
        maxWidth="sm"
        fullWidth={true}
      >
        <DialogTitle>
          Accept {propertyIds.length > 1 ? 'Properties' : 'Property'}
        </DialogTitle>
        <DialogContent>
          Would you like to accept{' '}
          {propertyIds.length > 1
            ? 'these properties'
            : 'this property'}
          ?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAccept(false)}>No</Button>
          <Button
            variant="contained"
            style={{ backgroundColor: '#3edf6e', color: 'white' }}
            onClick={(event) => {
              approveProperties({
                variables: {
                  propertyIds,
                  marketplaceURL,
                },
                ...approvalMutationCacheOptions(
                  'approveProperties',
                  propertyIds,
                ),
              })
                .then((result) => {
                  if (!result?.data?.approveProperties) {
                    enqueueSnackbar(
                      'Failed to accept property. Please try again.',
                      { variant: 'error', autoHideDuration: 4000 },
                    )
                    return
                  }
                  afterDecision(
                    event,
                    'success',
                    `${
                      propertyIds.length > 1
                        ? 'Properties'
                        : 'Property'
                    } has been accepted`,
                  )
                })
                .catch((error) => {
                  enqueueSnackbar(
                    error?.message ||
                      'Failed to accept property. Please try again.',
                    { variant: 'error', autoHideDuration: 4000 },
                  )
                })
              setOpenAccept(false)
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
export default withSession(SubmissionApprovalComponent)
